import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  trigger: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  // Styles the wrapper span around the trigger. Needed when the trigger must
  // fill its parent (the span is content-sized by default).
  triggerClassName?: string;
}

// Native focusables plus anything with an explicit tabindex. Disabled controls
// are excluded: they cannot receive focus, so a trigger wrapping only a
// disabled button must take the tab stop itself or the tooltip is unreachable
// by keyboard.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

// Portals `content` to a fixed-position layer at the trigger's bottom edge on
// hover or keyboard focus. Fixed positioning keeps tooltips visible across
// scroll containers (notably the Gantt's horizontal scroll). The wrapper
// provides only positioning — visual chrome (bg, border, padding, etc.) is the
// content's responsibility.
export const Tooltip = ({ trigger, content, disabled, triggerClassName }: TooltipProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const tooltipId = useId();

  // The trigger interactions are wired imperatively because the wrapper span's
  // focusability depends on the rendered trigger content: it only gets a tab
  // stop when the trigger has no focusable element of its own (a blanket
  // tabIndex around e.g. a Button trigger would create a double tab stop).
  // focusin/focusout bubble, so a focusable trigger opens the tooltip too.
  useLayoutEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const hasFocusableChild = el.querySelector(FOCUSABLE_SELECTOR) !== null;
    if (!hasFocusableChild) el.tabIndex = 0;
    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);
    el.addEventListener('mouseenter', open);
    el.addEventListener('mouseleave', close);
    el.addEventListener('focusin', open);
    el.addEventListener('focusout', close);
    return () => {
      el.removeEventListener('mouseenter', open);
      el.removeEventListener('mouseleave', close);
      el.removeEventListener('focusin', open);
      el.removeEventListener('focusout', close);
      if (!hasFocusableChild) el.removeAttribute('tabindex');
    };
  }, [disabled]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    update();
    globalThis.addEventListener('scroll', update, true);
    globalThis.addEventListener('resize', update);
    globalThis.addEventListener('keydown', closeOnEscape);
    return () => {
      globalThis.removeEventListener('scroll', update, true);
      globalThis.removeEventListener('resize', update);
      globalThis.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (disabled) return trigger;

  return (
    <>
      <span
        ref={triggerRef}
        className={triggerClassName}
        aria-describedby={isOpen ? tooltipId : undefined}
      >
        {trigger}
      </span>
      {isOpen &&
        pos &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className={styles.layer}
            style={{ top: pos.top, left: pos.left }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};
